import { AutoCompleteResult, AutoCompleteRequest } from '../../../types/common/auto-complete';
import { Address } from '../../../types/listing/address';
import { GeoCoordinates } from '../../../types/map/maps';
import { AddressComponentsConfig } from "../../../types/config/config";

const PLACE_DETAIL_FIELDS = ['addressComponents', 'location', 'formattedAddress'];

// the keystrokes leading up to a selection and the details lookup that follows it are billed as a single
// session, but only when they share a token. minted on the first keystroke, retired once the user picks a result.
let sessionToken: google.maps.places.AutocompleteSessionToken | undefined;

const getPlacesLibrary = async (): Promise<google.maps.PlacesLibrary> => {
    return await google.maps.importLibrary('places') as google.maps.PlacesLibrary;
}

// this is called by the searchable input
export const getGooglePlaces = async (request: AutoCompleteRequest): Promise<AutoCompleteResult[]> => {

    const input: string = request.value.trim();

    if (input.length === 0) {
        return [];
    }

    // todo: figure out how to actually remove the restrictions if they aren't set instead of defaulting to the US
    const includedRegionCodes: string[] = request.countryCodes ? request.countryCodes : ['us'];

    try {
        const { AutocompleteSuggestion, AutocompleteSessionToken } = await getPlacesLibrary();

        if (!sessionToken) {
            sessionToken = new AutocompleteSessionToken();
        }

        const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            includedRegionCodes,
            sessionToken
        });
        return convertGoogleResults(suggestions);
    } catch (error) {
        // for now, just send back a blank set of results. we should probably handle this gracefully with the component when we build the no results out
        // we need to send back a blank array though, otherwise the previous results stay up if they are still typing.
        return [];
    }
}

// convert the google results to get them in the form the searchable input needs to display
export const convertGoogleResults = (suggestions: google.maps.places.AutocompleteSuggestion[]) => {

    const converted: AutoCompleteResult[] = [];

    suggestions.forEach((suggestion: google.maps.places.AutocompleteSuggestion) => {
        const prediction = suggestion.placePrediction;
        if (prediction) {   // suggestions that aren't place predictions can't be resolved to an address
            converted.push({ name: prediction.text.text, value: prediction });
        }
    });

    return converted;
}

// a prediction only carries an id and its display text, so the address components come from a details lookup.
// going through toPlace() is what keeps that lookup inside the autocomplete session we opened.
const fetchPlaceDetails = async (prediction: google.maps.places.PlacePrediction): Promise<google.maps.places.Place> => {
    try {
        const { place } = await prediction.toPlace().fetchFields({ fields: PLACE_DETAIL_FIELDS });
        return place;
    } finally {
        sessionToken = undefined;   // the selection closes the session either way, the next keystroke opens a new one
    }
}

// this method is called once a user actually selects an address
export const createAddressFromGoogleResult = async (result: AutoCompleteResult, streetAddressSlug: string, siteId: string, addressComponentsConfig?: AddressComponentsConfig): Promise<Address> => {

    // if the user selects a result, we need to pull back the full details for it
    const place = await fetchPlaceDetails(result.value);
    return parseGoogleResult(place, streetAddressSlug, result.name, siteId, addressComponentsConfig);
}

const parseGoogleResult = (place: google.maps.places.Place, streetAddressSlug: string, streetName: string, siteId: string, addressComponentsConfig?: AddressComponentsConfig): Address => {
    if (place && place.addressComponents && place.location) {
        const coords: GeoCoordinates = convertGoogleLatLng(place.location);
        const addressComponents = place.addressComponents;

        const cityFieldName = (addressComponentsConfig && addressComponentsConfig.cityFieldName !== "") ? addressComponentsConfig.cityFieldName! : "locality";
        const city: string = extractResult(addressComponents, cityFieldName, 'longText');

        const postalCode: string = extractResult(addressComponents, 'postal_code', 'longText');
        let stateOrProvince: string = extractResult(addressComponents, 'administrative_area_level_1', 'shortText');
        if (stateOrProvince && stateOrProvince.length > 0) {
            stateOrProvince = stateOrProvince.split('.').join(""); // remove periods from state abbreviations, if they exist
            stateOrProvince = stateOrProvince.toUpperCase(); // make all abbreviations upper case
        }

        const country: string = extractResult(addressComponents, 'country', 'shortText');

        const streetNumber: string = extractResult(addressComponents, 'street_number', 'longText');
        const route: string = extractResult(addressComponents, 'route', 'longText');

        let street = streetAddressSlug;
        if (siteId === "ae-comm") {
            street = streetName;
        }
        else if (!!street) {
            // Swap string tokens
            street = street.replace('%(streetNumber)s', streetNumber);
            street = street.replace('%(route)s', route);
            // Remove any unmatched tokens and trim the spaces
            street = street.trim();
        }
        else {
            // fallback with the token string is undefined
            street = streetNumber ? streetNumber + ' ' + route : route;
        }

        // fallback for street
        // point of interest
        if (!street || street.length === 0) {
            street = extractResult(addressComponents, 'point_of_interest', 'longText');
        }
        // final fallback is the entire formatted address
        if (!street || street.length === 0) {
            street = place.formattedAddress || "";
        }

        return {
            'street': street,
            'city': city,
            'postalCode': postalCode,
            'stateOrProvince': stateOrProvince,
            'country': country,
            'lat': coords.lat,
            'lng': coords.lng
        }
    } else {
        throw new Error('Geocoding Failed');
    }
}

// convenience function to help us extract data from the google object
const extractResult = (addressComponents: google.maps.places.AddressComponent[], fieldName: string, use: 'longText' | 'shortText') => {
    let extracted: string = "";
    addressComponents.forEach((addressPart: google.maps.places.AddressComponent) => {
        if (addressPart.types && addressPart.types.indexOf(fieldName) > -1) {
            extracted = addressPart[use] || "";
        }
    });
    return extracted;
}

const convertGoogleLatLng = (latLng: google.maps.LatLng): GeoCoordinates => {
    return { lat: latLng.lat(), lng: latLng.lng() };
}
